import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Menu,
  MenuList,
  MenuItem as MenuListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CardActions,
  Rating,
  Tooltip,
  Fab,
  Paper,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  Avatar,
  Badge,
  Autocomplete,
  CircularProgress,
  Skeleton,
  LinearProgress,
  Grow,
  // Added table components
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  FlightTakeoff as FlightIcon,
  Hotel as HotelIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Schedule as ScheduleIcon,
  Restaurant as RestaurantIcon,
  DirectionsBus as BusIcon,
  PhotoCamera as PhotoIcon,
  LocalOffer as OfferIcon,
  CardTravel as TravelIcon,
  Language as PublicIcon,
  Business as BusinessIcon,
  PersonPin as PersonPinIcon,
  Insights as InsightsIcon,
  Assignment as AssignmentIcon,
  CardGiftcard,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  RocketLaunch,
  AutoAwesome,
  BarChart,
  LocalFireDepartment,
  Celebration,
  TravelExplore,
  ViewList as TableViewIcon,
  ViewModule as CardViewIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';

// Import Settings Context for real-time data
import { useSettings } from '../../contexts/SettingsContext';
import { 
  usePackagesData, 
  useClientsData, 
  useReservationsData, 
  useSuppliersData,
  addForfait as apiAddForfait,
  updateForfait as apiUpdateForfait,
  deleteForfait as apiDeleteForfait
} from '../../services/dataService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Add these image constants at the top of the file after imports
const packageImages = {
  'Istanbul': '/images/forfaits/istanbul.jpg',
  'Dubaï': '/images/forfaits/dubai.jpg',
  'Paris': '/images/forfaits/paris.jpg',
  'La Mecque': '/images/forfaits/mecca.jpg',
  'Marrakech': '/images/forfaits/marrakech.jpg',
  'Rome': '/images/forfaits/rome.jpg',
  'Istanbul, Turquie': '/images/forfaits/istanbul.jpg',
  'Dubaï, EAU': '/images/forfaits/dubai.jpg',
  'Paris, France': '/images/forfaits/paris.jpg',
  'La Mecque, Arabie Saoudite': '/images/forfaits/mecca.jpg',
  'Marrakech, Maroc': '/images/forfaits/marrakech.jpg',
  'Rome, Italie': '/images/forfaits/rome.jpg',
  'Culturel': '/images/forfaits/culturel.jpg',
  'Luxe': '/images/forfaits/luxe.jpg',
  'Religieux': '/images/forfaits/religieux.jpg',
  'Aventure': '/images/forfaits/aventure.jpg',
  'Business': '/images/forfaits/business.jpg',
  'Familial': '/images/forfaits/familial.jpg'
};

// Helper function to get image for a package
const getPackageImage = (forfait) => {
  // First check if there's a custom image URL
  if (forfait.image && forfait.image.trim() !== '') return forfait.image;
  
  // Then try to get image by destination
  if (packageImages[forfait.destination]) return packageImages[forfait.destination];
  
  // Then try to get by category
  if (packageImages[forfait.categorie]) return packageImages[forfait.categorie];
  
  // Try to get by first part of destination
  const destination = forfait.destination?.split(',')[0];
  if (packageImages[destination]) return packageImages[destination];
  
  // Default image
  return '/images/forfaits/default.jpg';
};

// Add a component for drag and drop image upload
const ImageDropzone = ({ value, onChange }) => {
  const [previewUrl, setPreviewUrl] = useState(value || '');
  const [isDragging, setIsDragging] = useState(false);
  
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files) {
      setIsDragging(true);
    }
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      handleFile(file);
    }
  }, []);
  
  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  };
  
  const handleFile = (file) => {
    // Check if file is an image (handle both browser File and Electron file objects)
    const fileType = file.type || file.fileType || '';
    const isImage = fileType.match('image.*') || file.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    
    if (!isImage) {
      alert('Veuillez sélectionner une image');
      return;
    }
    
    // Handle Electron file (already has base64 data)
    if (file.electronFile && file.data) {
      const base64Image = `data:${file.type || 'image/jpeg'};base64,${file.data}`;
      setPreviewUrl(base64Image);
      onChange(base64Image);
      return;
    }
    
    // Handle browser File/Blob
    if (file instanceof Blob || file instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Image = e.target.result;
        setPreviewUrl(base64Image);
        onChange(base64Image);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // NEW: Handle Electron native file dialog
  const handleElectronFileSelect = async () => {
    console.log('🟣 [FORFAITS] Opening Electron native file dialog for image...');
    
    try {
      if (window.electronAPI && window.electronAPI.openFileDialog) {
        const result = await window.electronAPI.openFileDialog({
          title: 'Sélectionner une image',
          filters: [
            { name: 'Images', extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'] },
            { name: 'Tous les fichiers', extensions: ['*'] }
          ]
        });
        
        if (!result.canceled && result.fileName) {
          console.log('✅ [FORFAITS] Image selected:', result.fileName);
          
          // Create File-like object
          const fileObject = {
            name: result.fileName,
            size: result.fileSize,
            type: result.fileType || 'image/jpeg',
            path: result.filePath,
            electronFile: true,
            data: result.fileData
          };
          
          handleFile(fileObject);
        }
      } else {
        console.warn('⚠️ [FORFAITS] Electron API not available, using HTML input');
        document.getElementById('image-upload')?.click();
      }
    } catch (error) {
      console.error('❌ [FORFAITS] Error opening file dialog:', error);
      document.getElementById('image-upload')?.click();
    }
  };
  
  // Completely separate handler for delete button
  const handleDeleteClick = () => {
    setPreviewUrl('');
    onChange('');
  };
  
  // If we have a preview, show the image with delete button
  if (previewUrl) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'white' }}>Image du forfait</Typography>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <img 
            src={previewUrl} 
            alt="Aperçu" 
            style={{ 
              width: '100%', 
              height: '200px', 
              objectFit: 'cover',
              borderRadius: '8px'
            }} 
          />
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8,
            }}
          >
            Supprimer
          </Button>
        </Box>
      </Box>
    );
  }
  
  // Otherwise show the dropzone
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'white' }}>Image du forfait</Typography>
      <Box
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          bgcolor: isDragging ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
          transition: 'all 0.2s',
          cursor: 'pointer'
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleElectronFileSelect}
      >
        <input
          type="file"
          id="image-upload"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
        <PhotoIcon sx={{ fontSize: 40, color: 'rgba(255, 255, 255, 0.7)', mb: 1 }} />
        <Typography variant="body1" sx={{ color: 'white' }}>
          Glissez une image ici ou cliquez pour sélectionner
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          JPG, PNG ou GIF • Max 5MB
        </Typography>
      </Box>
    </Box>
  );
};

const Forfaits = () => {
  // Get settings context for real-time updates
  const { settings, formatCurrency, formatDate, calculateTotalWithTax } = useSettings();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Fetch interconnected data
  const { packages, loading: packagesLoading, error: packagesError, stats: packagesStats } = usePackagesData();
  const { clients, loading: clientsLoading } = useClientsData();
  const { reservations, loading: reservationsLoading } = useReservationsData();
  const { suppliers, loading: suppliersLoading } = useSuppliersData();
  
  // Chargement optimisé - affichage immédiat des forfaits
  const isLoading = false;
  
  // Local state
  const [filteredForfaits, setFilteredForfaits] = useState([]);
  const [packageStats, setPackageStats] = useState({
    total: 0,
    active: 0,
    reservations: 0,
    interestedClients: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('all');
  const [filterPrix, setFilterPrix] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingForfait, setEditingForfait] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Add new state for details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [forfaitDetails, setForfaitDetails] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    destination: '',
    categorie: 'Culturel',
    prix: 0,
    duree: '',
    statut: 'Actif',
    description: '',
    fournisseurId: '',
    image: '', // Add image field
    inclusions: [] // Keep inclusions, remove clientsInteresses
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedForfait, setSelectedForfait] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  
  // Add state for confirmation dialog
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    forfaitId: null,
    hasReservations: false
  });

  // Function to force refresh all data
  const refreshAllData = useCallback(() => {
    // Trigger data refresh via custom event; hooks will refetch from API
    window.dispatchEvent(new CustomEvent('packageDataUpdate'));
    window.dispatchEvent(new CustomEvent('supplierDataUpdate'));
    window.dispatchEvent(new CustomEvent('reservationDataUpdate'));
    window.dispatchEvent(new CustomEvent('clientDataUpdate'));
    window.dispatchEvent(new CustomEvent('financialDataUpdate'));
    
    setSnackbar({
      open: true,
      message: 'Toutes les données ont été actualisées',
      severity: 'success'
    });
  }, []);

  // Add event listeners for real-time data updates from other pages
  useEffect(() => {
  // Removed localStorage storage events; rely on app custom events only
    const handleStorageChange = (e) => {
      if (e.key === 'packagesData') {
        // Let the main useEffect handle filtering via data hooks
        setSnackbar({
          open: true,
          message: 'Données des forfaits mises à jour en temps réel',
          severity: 'info'
        });
      } else if (e.key === 'suppliersData') {
        // Refresh supplier data as it might affect package relationships
        setSnackbar({
          open: true,
          message: 'Fournisseurs mis à jour - Relations forfaits actualisées',
          severity: 'info'
        });
        
        // If a package details dialog is open, refresh its data
        if (detailsDialogOpen && forfaitDetails) {
          handleOpenDetailsDialog(forfaitDetails);
        }
      } else if (e.key === 'reservationsData' || e.key === 'clientsData') {
        // For related data changes, show a notification
        setSnackbar({
          open: true,
          message: 'Données associées mises à jour - Interconnexion active',
          severity: 'info'
        });
        
        // If a package details dialog is open, refresh its data
        if (detailsDialogOpen && forfaitDetails) {
          handleOpenDetailsDialog(forfaitDetails);
        }
      }
    };

    // Function to handle custom events from other components
    const handlePackageEventUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Données des forfaits mises à jour en temps réel',
        severity: 'info'
      });
      
      // If a package details dialog is open, refresh its data
      if (detailsDialogOpen && forfaitDetails) {
          handleOpenDetailsDialog(forfaitDetails);
        }
    };

    const handleSupplierEventUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Fournisseurs mis à jour - Relations forfaits actualisées',
        severity: 'info'
      });
      
      // If a package details dialog is open, refresh its data
      if (detailsDialogOpen && forfaitDetails) {
        handleOpenDetailsDialog(forfaitDetails);
      }
    };

    const handleReservationEventUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Réservations mises à jour - Relations forfaits actualisées',
        severity: 'info'
      });
      
      // If a package details dialog is open, refresh its data
      if (detailsDialogOpen && forfaitDetails) {
        handleOpenDetailsDialog(forfaitDetails);
      }
    };
    
    const handleClientEventUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Clients mis à jour - Relations forfaits actualisées',
        severity: 'info'
      });
      
      // If a package details dialog is open, refresh its data
      if (detailsDialogOpen && forfaitDetails) {
        handleOpenDetailsDialog(forfaitDetails);
      }
    };

    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('packageDataUpdate', handlePackageEventUpdate);
    window.addEventListener('supplierDataUpdate', handleSupplierEventUpdate);
    window.addEventListener('reservationDataUpdate', handleReservationEventUpdate);
    window.addEventListener('clientDataUpdate', handleClientEventUpdate);

    // Clean up event listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('packageDataUpdate', handlePackageEventUpdate);
      window.removeEventListener('supplierDataUpdate', handleSupplierEventUpdate);
      window.removeEventListener('reservationDataUpdate', handleReservationEventUpdate);
      window.removeEventListener('clientDataUpdate', handleClientEventUpdate);
    };
  }, [detailsDialogOpen, forfaitDetails]);
  
  // Connect packages with clients and reservations
  useEffect(() => {
    if (!packagesLoading && !clientsLoading && !reservationsLoading && !suppliersLoading) {
      console.log("Forfaits component received data:");
      console.log("Packages with existing relationships:", packages);
      
      // Packages should already have client and reservation relationships 
      // from usePackagesData hook and resetAndInitializeData
      const safePackages = Array.isArray(packages) ? packages : [];
      const enhancedPackages = safePackages.map(forfait => {
        // These should already exist from usePackagesData
        const relatedReservations = forfait.reservations || [];
        const relatedClients = forfait.clients || [];
        
        return {
          ...forfait,
          // Ensure these fields exist for UI compatibility
          reservations: relatedReservations,
          clientsInteresses: relatedClients,
          popularityScore: (relatedReservations.length * 10) + (relatedClients.length * 5)
        };
      });
      
      // Calculate stats directly from the enhanced packages
      const totalPackages = enhancedPackages.length;
      const activePackages = enhancedPackages.filter(pkg => pkg.statut === 'Actif').length;
      const totalReservations = reservations.length;
      
      // Count unique clients from client relationships
      const allInterestedClientIds = new Set();
      enhancedPackages.forEach(pkg => {
        const clientArray = pkg.clientsInteresses || pkg.clients || [];
        clientArray.forEach(client => {
          if (client && client.id) {
            allInterestedClientIds.add(Number(client.id));
          }
        });
      });
      
      console.log("All interested clients:", allInterestedClientIds);
      
      // Update stats in local state to display in UI
      setPackageStats({
        total: totalPackages,
        active: activePackages,
        reservations: totalReservations,
        interestedClients: allInterestedClientIds.size
      });
      
      setFilteredForfaits(enhancedPackages);
    }
  }, [packages, clients, reservations, packagesLoading, clientsLoading, reservationsLoading, suppliersLoading]);
  
  // Filter packages based on search and filters
  useEffect(() => {
    if (!packagesLoading) {
      let filtered = [...packages];
      
      // Apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(f => 
          f.titre.toLowerCase().includes(search) || 
          f.destination.toLowerCase().includes(search) ||
          f.categorie.toLowerCase().includes(search)
        );
      }
      
      // Apply category filter
      if (filterCategorie !== 'all') {
        filtered = filtered.filter(f => f.categorie === filterCategorie);
      }
      
      // Apply price filter
      if (filterPrix !== 'all') {
        switch (filterPrix) {
          case 'under50k':
            filtered = filtered.filter(f => f.prix < 50000);
            break;
          case '50k-100k':
            filtered = filtered.filter(f => f.prix >= 50000 && f.prix <= 100000);
            break;
          case '100k-150k':
            filtered = filtered.filter(f => f.prix > 100000 && f.prix <= 150000);
            break;
          case 'over150k':
            filtered = filtered.filter(f => f.prix > 150000);
            break;
          default:
            break;
        }
      }
      
      setFilteredForfaits(filtered);
    }
  }, [searchTerm, filterCategorie, filterPrix, packages, packagesLoading]);

  // Pagination for card view
  const paginatedForfaits = filteredForfaits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Handler functions for forfait actions
  const handleViewForfait = (forfait) => {
    setForfaitDetails(forfait);
    setDetailsDialogOpen(true);
  };

  const handleEditForfait = (forfait) => {
    handleOpenDialog(forfait);
  };

  const handleOpenDialog = (forfait = null) => {
    if (forfait) {
      setEditingForfait(forfait);
      setFormData({
        titre: forfait.titre || '',
        destination: forfait.destination || '',
        categorie: forfait.categorie || 'Culturel',
        prix: forfait.prix || 0,
        duree: forfait.duree || '',
        statut: forfait.statut || 'Actif',
        description: forfait.description || '',
        fournisseurId: forfait.fournisseurId || '',
        image: forfait.image || '', // Add image field
        inclusions: forfait.inclusions || [] // Keep inclusions, remove clientsInteresses
      });
    } else {
      setEditingForfait(null);
      setFormData({
        titre: '',
        destination: '',
        categorie: 'Culturel',
        prix: 0,
        duree: '',
        statut: 'Actif',
        description: '',
        fournisseurId: '',
        image: '', // Add image field
        inclusions: [] // Keep inclusions, remove clientsInteresses
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingForfait(null);
    setFormData({
      titre: '',
      destination: '',
      categorie: 'Culturel',
      prix: 0,
      duree: '',
      statut: 'Actif',
      description: '',
      fournisseurId: '',
      image: '', // Add image field
      inclusions: [] // Keep inclusions, remove clientsInteresses
    });
  };

  const handleSaveForfait = async () => {
    try {
      if (editingForfait) {
        // Update via API
        await apiUpdateForfait(editingForfait.id, formData);
        setSnackbar({ open: true, message: 'Forfait mis à jour avec succès!', severity: 'success' });
      } else {
        // Create via API
        await apiAddForfait(formData);
        setSnackbar({ open: true, message: 'Nouveau forfait créé avec succès!', severity: 'success' });
      }
      // Ask data hooks to refresh
      window.dispatchEvent(new CustomEvent('packageDataUpdate'));
      handleCloseDialog();
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Erreur lors de l\'enregistrement du forfait', severity: 'error' });
    }
  };

  const handleDeleteForfait = (forfaitId) => {
    // Ensure ID is a number
    const packageId = Number(forfaitId);
    
    // Get current packages data
  const currentPackages = packages || [];
  const currentReservations = [];
    
    // Check if package has associated reservations
    const hasReservations = currentReservations.some(res => Number(res.forfaitId) === packageId);
    
    if (hasReservations) {
      // Open confirmation dialog instead of showing warning
      setDeleteConfirmDialog({
        open: true,
        forfaitId: packageId,
        hasReservations: true
      });
      
      // Close the menu if open
      if (anchorEl) {
        setAnchorEl(null);
        setSelectedForfait(null);
      }
      
      return;
    }
    
    // If no reservations, open normal confirmation
    setDeleteConfirmDialog({
      open: true,
      forfaitId: packageId,
      hasReservations: false
    });
    
    // Close the menu if open
    if (anchorEl) {
      setAnchorEl(null);
      setSelectedForfait(null);
    }
  };
  
  // Function to confirm deletion
  const confirmDeleteForfait = async () => {
    const packageId = deleteConfirmDialog.forfaitId;
    try {
      await apiDeleteForfait(packageId);
      
      // Force refresh packages data
      setTimeout(() => {
        // Refresh data after a short delay
        window.dispatchEvent(new CustomEvent('packageDataUpdate', {
          detail: { action: 'delete', data: { id: packageId }, timestamp: Date.now() }
        }));
      }, 100);
      
      setSnackbar({
        open: true,
        message: deleteConfirmDialog.hasReservations ? 'Forfait et ses réservations associées supprimés avec succès!' : 'Forfait supprimé avec succès!',
        severity: 'success'
      });
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Erreur lors de la suppression du forfait', severity: 'error' });
    }
    setDeleteConfirmDialog({ open: false, forfaitId: null, hasReservations: false });
  };
  
  // Function to cancel deletion
  const cancelDeleteForfait = () => {
    setDeleteConfirmDialog({
      open: false,
      forfaitId: null,
      hasReservations: false
    });
  };

  const handleMenuClick = (event, forfait) => {
    setAnchorEl(event.currentTarget);
    setSelectedForfait(forfait);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedForfait(null);
  };

  const getCategorieColor = (categorie) => {
    switch (categorie) {
      case 'Culturel': return 'primary';
      case 'Luxe': return 'secondary';
      case 'Religieux': return 'success';
      case 'Aventure': return 'warning';
      case 'Business': return 'info';
      case 'Familial': return 'error';
      default: return 'default';
    }
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'Actif': return 'success';
      case 'Inactif': return 'error';
      default: return 'default';
    }
  };
  
  // Get unique categories for filter
  const categories = [...new Set(packages.map(f => f.categorie))];
  
  // Define LoadingIndicator component
  const LoadingIndicator = () => (
    <Box sx={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2
    }}>
      <CircularProgress color="primary" size={60} />
      <Typography variant="h6" sx={{ color: 'rgba(51,65,85,0.8)' }}>
        Chargement des forfaits...
      </Typography>
    </Box>
  );

  // Define EnhancedStatCard component from Dashboard
  const EnhancedStatCard = ({ title, value, icon, gradient, delay, onClick, subtitle, chart, trend, progress }) => (
    <Grow in={true} style={{ transformOrigin: '0 0 0' }} timeout={0}>
      <Card
        onClick={onClick}
        sx={{
          height: '100%',
          background: gradient ? `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` : 'white',
          color: gradient ? 'white' : 'inherit',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'transform 0.3s, box-shadow 0.3s',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          } : {},
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ 
                opacity: gradient ? 0.9 : 0.7, 
                mb: 0.5, 
                fontWeight: 500,
                fontSize: { xs: '0.8rem', md: '0.875rem' }
              }}>
                {title}
              </Typography>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.75rem' }
              }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ 
                  opacity: gradient ? 0.9 : 0.7, 
                  mt: 0.5,
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}>
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Chip 
                  label={trend} 
                  size="small" 
                  sx={{ 
                    mt: 1,
                    backgroundColor: trend.startsWith('+') ? 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#059669') : 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#dc2626'),
                    color: gradient ? 'white' : 'white',
                    fontWeight: 600,
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    height: { xs: 20, md: 24 }
                  }}
                />
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: gradient ? 'rgba(255,255,255,0.2)' : 'primary.light',
                color: gradient ? 'white' : 'primary.main',
                width: { xs: 40, md: 48 },
                height: { xs: 40, md: 48 }
              }}
            >
              {icon}
            </Avatar>
          </Box>
          
          {progress && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  Progression
                </Typography>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: gradient ? 'white' : 'primary.main',
                  }
                }} 
              />
            </Box>
          )}
          
          {chart && (
            <Box sx={{ mt: 2, height: { xs: 40, md: 60 } }}>
              {chart}
            </Box>
          )}
        </CardContent>
      </Card>
    </Grow>
  );

  // Define FloatingActionCard component
  const FloatingActionCard = ({ title, icon, color, action, description, badge, onClick }) => {
    return (
      <Card sx={{
        background: `linear-gradient(135deg, ${color}15, white)`,
        border: `1px solid ${color}30`,
        borderRadius: { xs: 3, md: 4 },
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        height: '100%', // Ensure consistent height
      }} onClick={onClick}>
        <CardContent sx={{ 
          p: { xs: 2, sm: 2.5, md: 3 }, 
          position: 'relative', 
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 1, md: 2 }, 
            mb: { xs: 1.5, md: 2 },
            flexWrap: 'wrap'
          }}>
            <Avatar className="action-icon" sx={{ 
              background: `linear-gradient(135deg, ${color}, ${color}cc)`,
              width: { xs: 48, sm: 52, md: 56 },
              height: { xs: 48, sm: 52, md: 56 },
              boxShadow: `0 8px 25px ${color}40`,
              '& .MuiSvgIcon-root': {
                fontSize: { xs: 26, sm: 28, md: 30 }
              }
            }}>
              {icon}
            </Avatar>
            {badge && (
              <Chip 
                label={badge} 
                size="small" 
                sx={{ 
                  background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                  color: 'white',
                  fontWeight: 600,
                  height: { xs: '20px', md: '24px' },
                  fontSize: { xs: '0.7rem', md: '0.75rem' }
                }}
              />
            )}
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              mb: { xs: 0.5, md: 1 }, 
              color: 'rgba(51,65,85,0.9)',
              lineHeight: 1.3,
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(51,65,85,0.7)',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              display: { xs: '-webkit-box', md: 'block' },
              WebkitLineClamp: { xs: 2, md: 'none' },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  // If loading, show loading state
  if (isLoading) {
    return <LoadingIndicator />;
  }
  
  // If error, show error state
  if (packagesError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Erreur lors du chargement des forfaits: {packagesError}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
        >
          Réessayer
        </Button>
      </Box>
    );
  }

  // Function to format currency correctly
  const formatCurrencyForPDF = (amount) => {
    // Convert number to string with spaces as thousand separators
    const numberPart = Math.round(amount || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${numberPart} DZD`;
  };

  const exportForfaitsToPDF = () => {
    try {
      // Create a new PDF document
      const doc = new jsPDF('landscape');
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const successColor = [16, 185, 129]; // green
      const warningColor = [245, 158, 11]; // amber
      const errorColor = [239, 68, 68]; // red
      const textColor = [31, 41, 55]; // dark gray
      
      // Add header
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Add agency logo and name
      if (settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
      }
      
      // Add title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text("Catalogue des Forfaits", pageWidth - margin, 17, { align: 'right' });
      
      // Add date and filters
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(10);
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, margin, 35);
      doc.text(`Filtres: Catégorie: ${filterCategorie === 'all' ? 'Toutes' : filterCategorie} | Prix: ${filterPrix === 'all' ? 'Tous' : filterPrix}`, margin, 42);
      
      // Add summary section
      doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, 48, pageWidth - margin, 48);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Résumé des Forfaits", margin, 58);
      
      // Calculate some key metrics
      const totalForfaits = filteredForfaits.length;
      const activeForfaits = filteredForfaits.filter(f => f.statut === 'Actif').length;
      
      // Get categories and their counts
      const categoryCounts = {};
      filteredForfaits.forEach(forfait => {
        if (!categoryCounts[forfait.categorie]) {
          categoryCounts[forfait.categorie] = 0;
        }
        categoryCounts[forfait.categorie]++;
      });
      
      // Calculate average price
      const totalPrice = filteredForfaits.reduce((sum, forfait) => sum + (forfait.prix || 0), 0);
      const averagePrice = totalForfaits > 0 ? totalPrice / totalForfaits : 0;
      
      const stats = [
        { label: "Total des Forfaits", value: `${totalForfaits}` },
        { label: "Forfaits Actifs", value: `${activeForfaits} (${Math.round(activeForfaits / totalForfaits * 100 || 0)}%)` },
        { label: "Prix Moyen", value: formatCurrencyForPDF(averagePrice) },
        { label: "Destinations Disponibles", value: `${new Set(filteredForfaits.map(f => f.destination)).size}` },
        { label: "Nombre de Catégories", value: `${Object.keys(categoryCounts).length}` }
      ];
      
      doc.autoTable({
        startY: 62,
        head: [['Indicateur', 'Valeur']],
        body: stats.map(item => [item.label, item.value]),
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add categories summary
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Répartition par Catégorie", margin, doc.lastAutoTable.finalY + 15);
      
      const categoryStats = Object.entries(categoryCounts)
        .map(([category, count]) => ({
          category,
          count,
          percentage: (count / totalForfaits * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Catégorie', 'Nombre de Forfaits', 'Pourcentage']],
        body: categoryStats.map(item => [
          item.category, 
          item.count.toString(),
          `${item.percentage}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: secondaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add destinations summary
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Destinations", margin, doc.lastAutoTable.finalY + 15);
      
      const destinationCounts = {};
      filteredForfaits.forEach(f => {
        if (!destinationCounts[f.destination]) {
          destinationCounts[f.destination] = 0;
        }
        destinationCounts[f.destination]++;
      });
      
      const destinationStats = Object.entries(destinationCounts)
        .map(([destination, count]) => ({
          destination,
          count,
          percentage: (count / totalForfaits * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Destination', 'Nombre de Forfaits', 'Pourcentage']],
        body: destinationStats.map(item => [
          item.destination, 
          item.count.toString(),
          `${item.percentage}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: successColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add forfaits details
      doc.addPage();
      
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, 10, 30, 10, 1, 1, 'F');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("TravelIT", margin + 6, 17);
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text("Liste des Forfaits", pageWidth - margin, 17, { align: 'right' });
      
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(12);
      doc.text("Détails de tous les forfaits disponibles", margin, 40);
      
      // Add forfaits table - format matches our new table view
      const forfaitsTableData = filteredForfaits.map(f => {
        // Find supplier name if available
        let supplierName = 'Non spécifié';
        if (f.fournisseurId && suppliers) {
          const supplier = suppliers.find(s => s.id === f.fournisseurId);
          if (supplier) {
            supplierName = supplier.nom;
          }
        }
        
        // Get number of reservations
        const reservationsCount = f.reservations?.length || 0;
        
        return [
          // Combine title and destination
          `${f.titre}\n${f.destination}`,
          f.categorie,
          f.duree,
          formatCurrencyForPDF(f.prix),
          f.statut || 'N/A',
          `${reservationsCount} réservation${reservationsCount !== 1 ? 's' : ''}`,
          supplierName
        ];
      });
      
      doc.autoTable({
        startY: 50,
        head: [['Titre / Destination', 'Catégorie', 'Durée', 'Prix', 'Statut', 'Réservations', 'Fournisseur']],
        body: forfaitsTableData,
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 25 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 30 },
          6: { cellWidth: 35 }
        },
        margin: { left: margin, right: margin },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: function (data) {
          // Add header on each page
          doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.rect(0, 0, pageWidth, 25, 'F');
          
          // Add agency logo and name
          if (settings.agencyLogo) {
            try {
              // Add the agency logo from base64 string
              doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
              
              // Add agency name
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
            } catch (logoError) {
              console.error('Error adding logo to PDF:', logoError);
              // Fallback to text only
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
            }
          } else {
            // No logo, just add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.text("Liste des Forfaits", pageWidth - margin, 17, { align: 'right' });
          
          // Add footer
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Page ${pageNumber}`, margin, pageHeight - 10);
          doc.text(`${settings.appName} - Système de Gestion pour Agence de Voyage`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        }
      });
      
      // Save the PDF
      doc.save('catalogue-forfaits.pdf');
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Export PDF réussi',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'export PDF',
        severity: 'error'
      });
    }
  };

  // Function to handle client selection
  const handleClientSelection = (event, newValue) => {
    setFormData({ 
      ...formData, 
      clientsInteresses: newValue 
    });
  };

  // Add new function to handle opening details dialog
  const handleOpenDetailsDialog = (forfait) => {
    // Only open if we have a valid forfait object
    if (forfait) {
      console.log("Opening details for forfait:", forfait);
      
      // Use current in-memory data (replace with API calls if necessary)
      const latestPackages = packages || [];
      const latestReservations = reservations || [];
      const latestClients = clients || [];
      const latestSuppliers = suppliers || [];
      
      // Find the most up-to-date version of this forfait
      const updatedForfait = latestPackages.find(p => p.id === forfait.id) || forfait;
      
      // Get the full client data for any interested clients
      let interestedClientsData = [];
      
      if (updatedForfait.clientsInteresses && Array.isArray(updatedForfait.clientsInteresses)) {
        console.log("Client interests found:", updatedForfait.clientsInteresses);
        
        // If clientsInteresses contains full client objects
        if (updatedForfait.clientsInteresses.length > 0 && typeof updatedForfait.clientsInteresses[0] === 'object') {
          interestedClientsData = [...updatedForfait.clientsInteresses];
        } 
        // If clientsInteresses contains only client IDs
        else if (updatedForfait.clientsInteresses.length > 0 && (typeof updatedForfait.clientsInteresses[0] === 'number' || typeof updatedForfait.clientsInteresses[0] === 'string')) {
          interestedClientsData = updatedForfait.clientsInteresses.map(clientId => {
            const client = latestClients.find(c => c.id === clientId || c.id === Number(clientId));
            return client || { id: clientId, nom: 'Client', prenom: '' };
          });
        }
      }
      
      // Get any reservations associated with this forfait
      const associatedReservations = latestReservations.filter(res => 
        res.forfaitId === updatedForfait.id || res.forfait === updatedForfait.titre
      );
      
      console.log("Found associated reservations:", associatedReservations);
      console.log("Found interested clients:", interestedClientsData);
      
      // Get supplier information if available
      let supplierInfo = null;
      if (updatedForfait.fournisseurId) {
        supplierInfo = latestSuppliers.find(s => s.id === updatedForfait.fournisseurId);
        console.log("Found supplier info:", supplierInfo);
      }
      
      // Make a clean copy of the forfait data to display with enhanced data
      const cleanForfait = {
        ...updatedForfait,
        // Don't add any default values - only use what's in the actual data
        inclusions: updatedForfait.inclusions && Array.isArray(updatedForfait.inclusions) ? [...updatedForfait.inclusions] : [],
        // Add the full client objects
        clientsInteresses: interestedClientsData,
        // Add any associated reservations
        reservations: associatedReservations,
        // Add supplier information
        supplier: supplierInfo
      };
      
      console.log("Opening details dialog with data:", cleanForfait);
      setForfaitDetails(cleanForfait);
      setDetailsDialogOpen(true);
      handleMenuClose();
    }
  };

  // Add new function to close details dialog
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setForfaitDetails(null);
  };

  // Add a new function to handle inclusions selection
  const handleInclusionsChange = (event, newValue) => {
    setFormData({
      ...formData,
      inclusions: newValue
    });
  };

  // Common inclusions options for the dropdown
  const inclusionsOptions = [
    'Vol aller-retour',
    'Vol Business',
    'Hébergement',
    'Hôtel 5★',
    'Hôtel 4★',
    'Transferts',
    'Transferts VIP',
    'Guide touristique',
    'Repas complets',
    'Petit-déjeuner',
    'Excursions guidées',
    'Assurance voyage',
    'Transport local',
    'WiFi gratuit'
  ];

  return (
    <Box sx={{ p: { xs: 0, md: 3 } }}>
      {/* Header Section */}
      <Box sx={{ 
        mb: { xs: 3, md: 5 },
        p: { xs: 2, md: 0 }
      }} className="flex justify-between items-center">
        <div>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            color: 'white',
            mb: 1.5,
            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
            letterSpacing: '-0.01em'
          }}>
            Gestion des Forfaits
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'rgba(255,255,255,0.6)',
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
          }}>
            Créez et gérez vos forfaits de voyage avec des destinations attractives
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            px: 3,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
            },
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)'
          }}
        >
          Nouveau Forfait
        </Button>
        
      </Box>

      {/* Main Content Container */}
      <Box sx={{ px: { xs: 2, md: 0 } }} className="space-y-6">
        {/* Quick Actions Section */}
        <Box sx={{ mb: { xs: 3, md: 4 } }} className="space-y-2">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <RocketLaunch sx={{ mr: 1.5, color: '#6366f1', fontSize: { xs: 26, md: 30 } }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
              Actions Rapides
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={6} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 25px rgba(99, 102, 241, 0.4)'
                  },
                  height: '100%'
                }} onClick={() => handleOpenDialog()}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Avatar sx={{
                        bgcolor: '#6366f1',
                        width: 56,
                        height: 56,
                        mb: 2
                      }}>
                        <AddIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                        Nouveau Forfait
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Créer un nouveau forfait de voyage
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)'
                  },
                  height: '100%'
                }} onClick={exportForfaitsToPDF}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Avatar sx={{
                        bgcolor: '#10b981',
                        width: 56,
                        height: 56,
                        mb: 2
                      }}>
                        <ExportIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                        Exporter Catalogue
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Générer le catalogue en PDF
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 25px rgba(245, 158, 11, 0.4)'
                  },
                  height: '100%'
                }} onClick={() => {}}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Avatar sx={{
                        bgcolor: '#f59e0b',
                        width: 56,
                        height: 56,
                        mb: 2
                      }}>
                        <TravelExplore />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                        Gérer Destinations
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Consulter les destinations
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)'
                  },
                  height: '100%'
                }} onClick={() => refreshAllData()}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Avatar sx={{
                        bgcolor: '#8b5cf6',
                        width: 56,
                        height: 56,
                        mb: 2
                      }}>
                        <BarChart />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                        Statistiques
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Analyser les performances
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Box>
        
        {/* Key Metrics Section */}
        <Box sx={{ mb: { xs: 3, md: 4 } }} className="space-y-2">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AutoAwesome sx={{ mr: 1.5, color: '#10b981', fontSize: { xs: 26, md: 30 } }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
              Métriques des Forfaits
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Total des Forfaits</p>
                    <p className="text-2xl font-bold text-white mt-2">{packageStats.total || 0}</p>
                    <p className="text-white/60 text-xs mt-1">
                      Tous forfaits confondus
                    </p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700">
                    <TravelIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Forfaits Actifs</p>
                    <p className="text-2xl font-bold text-white mt-2">{packageStats.active || 0}</p>
                    <p className="text-white/60 text-xs mt-1">
                      Disponibles à la réservation
                    </p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/20 text-white mt-2">
                      {packageStats.total > 0 ? Math.round((packageStats.active / packageStats.total) * 100) : 0}%
                    </span>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-green-600 to-green-700">
                    <CheckCircleIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Réservations</p>
                    <p className="text-2xl font-bold text-white mt-2">{packageStats.reservations || 0}</p>
                    <p className="text-white/60 text-xs mt-1">
                      Réservations effectuées
                    </p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-amber-600 to-amber-700">
                    <AssignmentIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Clients Intéressés</p>
                    <p className="text-2xl font-bold text-white mt-2">{packageStats.interestedClients || 0}</p>
                    <p className="text-white/60 text-xs mt-1">
                      Base clients intéressés
                    </p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-purple-600 to-purple-700">
                    <PeopleIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            </Grid>
          </Grid>
        </Box>
        
        {/* Search and Filters Section */}
        <Box sx={{ mb: { xs: 3, md: 4 } }} className="space-y-2">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SearchIcon sx={{ mr: 1.5, color: '#f59e0b', fontSize: { xs: 26, md: 30 } }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
              Recherche de Forfaits
            </Typography>
          </Box>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 mb-3"
          >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Rechercher un forfait..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1
                      },
                      '& .MuiInputBase-input': {
                        color: 'white'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Catégorie</InputLabel>
                    <Select
                      value={filterCategorie}
                      label="Catégorie"
                      onChange={(e) => setFilterCategorie(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 2,
                            mt: 1,
                            '& .MuiMenuItem-root': {
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                              },
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.3)'
                                }
                              }
                            }
                          }
                        }
                      }}
                      sx={{ 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    >
                      <MenuItem value="all">Toutes</MenuItem>
                      <MenuItem value="Culturel">Culturel</MenuItem>
                      <MenuItem value="Aventure">Aventure</MenuItem>
                      <MenuItem value="Religieux">Religieux</MenuItem>
                      <MenuItem value="Familial">Familial</MenuItem>
                      <MenuItem value="Business">Business</MenuItem>
                      <MenuItem value="Luxe">Luxe</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Gamme de Prix</InputLabel>
                    <Select
                      value={filterPrix}
                      label="Gamme de Prix"
                      onChange={(e) => setFilterPrix(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 2,
                            mt: 1,
                            '& .MuiMenuItem-root': {
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                              },
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.3)'
                                }
                              }
                            }
                          }
                        }
                      }}
                      sx={{ 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    >
                      <MenuItem value="all">Tous prix</MenuItem>
                      <MenuItem value="under50k">&lt; 50,000 DZD</MenuItem>
                      <MenuItem value="50k-100k">50,000 - 100,000 DZD</MenuItem>
                      <MenuItem value="100k-150k">100,000 - 150,000 DZD</MenuItem>
                      <MenuItem value="over150k">&gt; 150,000 DZD</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={2}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<FilterIcon />}
                      sx={{ 
                        borderRadius: 2, 
                        flex: 1,
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      Filtrer
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      sx={{ 
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        flex: 1,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #d97706, #b45309)'
                        },
                        boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
                      }}
                    >
                      Rechercher
                    </Button>
                  </Box>
                </Grid>
              </Grid>
          </motion.div>
        </Box>
        {/* Forfaits Grid Section */}
        <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CardGiftcard sx={{ mr: 1.5, color: '#8b5cf6', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Catalogue des Forfaits
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* View Toggle Buttons */}
            <Box sx={{ display: 'flex', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: 2, overflow: 'hidden' }}>
              <Button
                variant={viewMode === 'table' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('table')}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 1,
                  borderRadius: 0,
                  border: 'none',
                  backgroundColor: viewMode === 'table' ? 'rgba(139, 92, 246, 0.8)' : 'transparent',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: viewMode === 'table' ? 'rgba(139, 92, 246, 1)' : 'rgba(255, 255, 255, 0.1)',
                    border: 'none'
                  }
                }}
              >
                <TableViewIcon fontSize="small" />
              </Button>
              <Button
                variant={viewMode === 'card' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('card')}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 1,
                  borderRadius: 0,
                  border: 'none',
                  backgroundColor: viewMode === 'card' ? 'rgba(139, 92, 246, 0.8)' : 'transparent',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: viewMode === 'card' ? 'rgba(139, 92, 246, 1)' : 'rgba(255, 255, 255, 0.1)',
                    border: 'none'
                  }
                }}
              >
                <CardViewIcon fontSize="small" />
              </Button>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={exportForfaitsToPDF}
              sx={{ 
                borderRadius: 2,
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'white',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Exporter PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                },
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
              }}
            >
              Nouveau Forfait
            </Button>
          </Box>
        </Box>

        {filteredForfaits.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 text-center"
          >
            <div className="flex flex-col items-center justify-center">
              <CardGiftcard className="h-16 w-16 text-white/30 mb-4" />
              <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                Aucun forfait trouvé
              </Typography>
              <Typography variant="body2" sx={{ mb: 4, color: 'rgba(255, 255, 255, 0.6)' }}>
                Essayez de modifier vos filtres ou créez un nouveau forfait
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                  },
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
                  px: 4,
                  py: 1.5
                }}
              >
                Créer un forfait
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden relative">
            {/* Table View */}
            {viewMode === 'table' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-4 text-left text-white font-semibold">Titre / Destination</th>
                      <th className="px-6 py-4 text-left text-white font-semibold">Catégorie</th>
                      <th className="px-6 py-4 text-left text-white font-semibold">Durée</th>
                      <th className="px-6 py-4 text-left text-white font-semibold">Prix</th>
                      <th className="px-6 py-4 text-left text-white font-semibold">Statut</th>
                      <th className="px-6 py-4 text-left text-white font-semibold">Réservations</th>
                      <th className="px-6 py-4 text-left text-white font-semibold">Fournisseur</th>
                      <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                  {filteredForfaits
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((forfait) => (
                      <motion.tr
                        key={forfait.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-white/5 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <img
                              src={getPackageImage(forfait)}
                              alt={forfait.titre}
                              className="w-12 h-12 rounded-lg object-cover shadow-md"
                              onError={(e) => {
                                // Use a simple SVG placeholder to avoid infinite loops
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjNEY0NkU1IiByeD0iOCIvPgo8cGF0aCBkPSJNMTYgMTZIMzJWMzJIMTZWMTZaIiBmaWxsPSIjMDA3QkZGIi8+CjxwYXRoIGQ9Ik0yMCAyMEgyOFYyOEgyMFYyMFoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=';
                                // Prevent further error events on this element
                                e.target.onError = null;
                              }}
                            />
                            <div>
                              <div className="font-semibold text-white mb-1">
                                {forfait.titre}
                              </div>
                              <div className="flex items-center space-x-1">
                                <LocationIcon className="h-3.5 w-3.5 text-red-400" />
                                <div className="text-xs text-white/70">
                                  {forfait.destination}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Chip 
                            label={forfait.categorie} 
                            color={getCategorieColor(forfait.categorie)}
                            size="small"
                            sx={{ borderRadius: 1.5, fontWeight: 'medium' }}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-1">
                            <TimeIcon className="h-4 w-4 text-blue-400" />
                            <div className="text-white font-medium">
                              {forfait.duree}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-bold text-blue-400">
                            {formatCurrency(forfait.prix)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Chip 
                            label={forfait.statut} 
                            color={getStatutColor(forfait.statut)}
                            size="small"
                            sx={{ borderRadius: 1.5, minWidth: 80 }}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <div className="relative">
                              <FlightIcon className="h-5 w-5 text-white/60" />
                              <span className="absolute -top-2 -right-2 h-4 w-4 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                                {forfait.reservations?.length || 0}
                              </span>
                            </div>
                            <div className="text-white font-medium text-sm">
                              {forfait.reservations?.length || 0} réservation{forfait.reservations?.length !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {forfait.fournisseurId && suppliers ? 
                            (() => {
                              const supplier = suppliers.find(s => s.id === forfait.fournisseurId);
                              return supplier ? (
                                <div className="flex items-center space-x-1">
                                  <BusinessIcon className="h-4 w-4 text-blue-400" />
                                  <div className="text-white font-medium">
                                    {supplier.nom}
                                  </div>
                                </div>
                              ) : <span className="text-white/60">Non spécifié</span>;
                            })() : <span className="text-white/60">Non spécifié</span>
                          }
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleOpenDetailsDialog(forfait)}
                              className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                              title="Voir les détails"
                            >
                              <VisibilityIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleOpenDialog(forfait)}
                              className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                              title="Modifier"
                            >
                              <EditIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteForfait(forfait.id)}
                              className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                              title="Supprimer"
                            >
                              <DeleteIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Card View */}
            {viewMode === 'card' && (
              <div className="p-6">
                {filteredForfaits.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="flex flex-col items-center">
                      <CardGiftcard className="h-16 w-16 text-white/40 mb-4" />
                      <h3 className="text-xl font-medium text-white/60 mb-2">Aucun forfait trouvé</h3>
                      <p className="text-white/40 text-lg">
                        {searchTerm || filterCategorie !== 'all' || filterPrix !== 'all'
                          ? 'Essayez de modifier vos critères de recherche.' 
                          : 'Commencez par ajouter votre premier forfait.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <Grid container spacing={3}>
                    {paginatedForfaits.map((forfait, index) => (
                      <Grid item xs={12} sm={6} lg={4} key={forfait.id}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card sx={{ 
                            background: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: 3,
                            p: 3,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 24px rgba(139, 92, 246, 0.2)',
                              background: 'rgba(255, 255, 255, 0.08)'
                            }
                          }}>
                            {/* Package Header with Image */}
                            <Box sx={{ mb: 3 }}>
                              {forfait.image && (
                                <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', height: 120 }}>
                                  <img 
                                    src={forfait.image} 
                                    alt={forfait.titre}
                                    style={{ 
                                      width: '100%', 
                                      height: '100%', 
                                      objectFit: 'cover',
                                      borderRadius: '8px'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </Box>
                              )}
                              <Typography variant="h6" sx={{ 
                                color: 'white', 
                                fontWeight: 600, 
                                fontSize: '1.1rem',
                                lineHeight: 1.2,
                                mb: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>
                                {forfait.titre || 'Titre non renseigné'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LocationIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.8)', 
                                  fontSize: '0.9rem' 
                                }}>
                                  {forfait.destination || 'Destination non renseignée'}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Package Details */}
                            <Box sx={{ mb: 3, flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <CalendarIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.8)', 
                                  fontSize: '0.85rem' 
                                }}>
                                  Durée: {forfait.duree || 'Non spécifiée'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <BusinessIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.8)', 
                                  fontSize: '0.85rem' 
                                }}>
                                  {forfait.fournisseurId ? suppliers?.find(s => s.id === forfait.fournisseurId)?.nom || 'Fournisseur inconnu' : 'Aucun fournisseur'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <MoneyIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                <Typography variant="body2" sx={{ 
                                  color: 'white', 
                                  fontSize: '1rem', 
                                  fontWeight: 600 
                                }}>
                                  {formatCurrency(forfait.prix)}
                                </Typography>
                              </Box>
                              {forfait.description && (
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.7)', 
                                  fontSize: '0.85rem',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  mb: 2
                                }}>
                                  {forfait.description}
                                </Typography>
                              )}
                            </Box>

                            {/* Status and Category Badges */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                              <Chip
                                label={forfait.statut || 'Inactif'}
                                size="small"
                                sx={{
                                  backgroundColor: forfait.statut === 'Actif' ? 'rgba(34, 197, 94, 0.8)' : 'rgba(107, 114, 128, 0.8)',
                                  color: 'white',
                                  fontWeight: 500,
                                  fontSize: '0.75rem'
                                }}
                              />
                              <Chip
                                label={forfait.categorie || 'Général'}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(139, 92, 246, 0.8)',
                                  color: 'white',
                                  fontWeight: 500,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </Box>

                            {/* Actions */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 'auto' }}>
                              <Tooltip title="Voir les détails">
                              <IconButton
                                size="small"
                                  onClick={() => handleViewForfait(forfait)}
                                  sx={{
                                    color: 'rgb(34, 197, 94)',
                                    bgcolor: 'rgba(34, 197, 94, 0.1)',
                                    '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.2)' },
                                    width: 36,
                                    height: 36
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Modifier">
                              <IconButton
                                size="small"
                                  onClick={() => handleEditForfait(forfait)}
                                  sx={{
                                    color: 'rgb(245, 158, 11)',
                                    bgcolor: 'rgba(245, 158, 11, 0.1)',
                                    '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.2)' },
                                    width: 36,
                                    height: 36
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Supprimer">
                              <IconButton
                                size="small"
                                  onClick={() => handleDeleteForfait(forfait)}
                                  sx={{
                                    color: 'rgb(239, 68, 68)',
                                    bgcolor: 'rgba(239, 68, 68, 0.1)',
                                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
                                    width: 36,
                                    height: 36
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </div>
            )}
            <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
              <div className="text-sm text-white/60">
                Affichage de {(page * rowsPerPage) + 1} à {Math.min((page + 1) * rowsPerPage, filteredForfaits.length)} sur {filteredForfaits.length} forfaits
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setPage(Math.min(Math.ceil(filteredForfaits.length / rowsPerPage) - 1, page + 1))}
                  disabled={page >= Math.ceil(filteredForfaits.length / rowsPerPage) - 1}
                  className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </Box>
      
      {/* Forfait Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          {editingForfait ? 'Modifier le forfait' : 'Nouveau forfait'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
                label="Titre"
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
                label="Destination"
                value={formData.destination}
                onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="categorie-label" sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Catégorie</InputLabel>
                <Select
                  labelId="categorie-label"
                  value={formData.categorie}
                  label="Catégorie"
                  onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="Culturel">Culturel</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                  <MenuItem value="Luxe">Luxe</MenuItem>
                  <MenuItem value="Religieux">Religieux</MenuItem>
                  <MenuItem value="Aventure">Aventure</MenuItem>
                  <MenuItem value="Familial">Familial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
                label="Prix (DZD)"
                type="number"
                value={formData.prix}
                onChange={(e) => setFormData({ ...formData, prix: Number(e.target.value) })}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>DZD</InputAdornment>,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel id="duree-label" sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Durée</InputLabel>
                <Select
                  labelId="duree-label"
                  id="duree-select"
                  name="duree"
                  value={formData.duree}
                  onChange={(e) => setFormData({ ...formData, duree: e.target.value })}
                  label="Durée"
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="3 jours / 2 nuits">3 jours / 2 nuits</MenuItem>
                  <MenuItem value="4 jours / 3 nuits">4 jours / 3 nuits</MenuItem>
                  <MenuItem value="5 jours / 4 nuits">5 jours / 4 nuits</MenuItem>
                  <MenuItem value="6 jours / 5 nuits">6 jours / 5 nuits</MenuItem>
                  <MenuItem value="7 jours / 6 nuits">7 jours / 6 nuits</MenuItem>
                  <MenuItem value="8 jours / 7 nuits">8 jours / 7 nuits</MenuItem>
                  <MenuItem value="10 jours / 9 nuits">10 jours / 9 nuits</MenuItem>
                  <MenuItem value="14 jours / 13 nuits">14 jours / 13 nuits</MenuItem>
                  <MenuItem value="Weekend (2 jours)">Weekend (2 jours)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="statut-label" sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Statut</InputLabel>
                <Select
                  labelId="statut-label"
                  value={formData.statut}
                  label="Statut"
                  onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="Actif">Actif</MenuItem>
                  <MenuItem value="Inactif">Inactif</MenuItem>
                  <MenuItem value="Complet">Complet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Replace URL image field with drag and drop */}
            <Grid item xs={12}>
              <ImageDropzone 
                value={formData.image} 
                onChange={(base64Image) => setFormData({ ...formData, image: base64Image })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={4}
              />
            </Grid>
            
            {/* Inclusions multi-select */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={inclusionsOptions}
                value={formData.inclusions}
                onChange={handleInclusionsChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Inclusions du forfait"
                    placeholder="Sélectionner ce qui est inclus"
                    helperText="Sélectionnez tout ce qui est inclus dans ce forfait"
                    sx={{
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-focused': {
                          color: 'rgba(255, 255, 255, 0.9)'
                        }
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'rgba(255, 255, 255, 0.7)'
                      }
                    }}
                  />
                )}
                renderTags={(selectedValues, getTagProps) =>
                  selectedValues.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      variant="outlined"
                      size="small"
                      sx={{ m: 0.5 }}
                    />
                  ))
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
                      {option}
                    </Box>
                  </Box>
                )}
              />
            </Grid>
            
            {/* Remove the clients interested field */}
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Fournisseur</InputLabel>
                <Select
                  label="Fournisseur"
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                  value={formData.fournisseurId || ''}
                  onChange={(e) => setFormData({ ...formData, fournisseurId: e.target.value })}
                >
                  <MenuItem value="">Aucun fournisseur</MenuItem>
                  {suppliers && suppliers.filter(s => s.statut === 'Actif').map(supplier => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.nom} ({supplier.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2, color: 'white' }}>
            Annuler
          </Button>
          <Button 
            onClick={handleSaveForfait}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
              color: 'white'
            }}
          >
            {editingForfait ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuListItem onClick={() => handleOpenDetailsDialog(selectedForfait)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Voir détails</ListItemText>
        </MenuListItem>
        <MenuListItem onClick={() => {
          handleOpenDialog(selectedForfait);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Modifier</ListItemText>
        </MenuListItem>
        <Divider />
        <MenuListItem onClick={() => {
          handleDeleteForfait(selectedForfait?.id);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Supprimer</ListItemText>
        </MenuListItem>
      </Menu>
      
      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            minHeight: '600px'
          }
        }}
      >
        {forfaitDetails && (
          <>
            <DialogTitle sx={{ pb: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FlightIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>
                    {forfaitDetails.titre}
                </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {forfaitDetails.destination}
                  </Typography>
                </Box>
                <Box sx={{ 
                  px: 2, 
                  py: 1, 
                  borderRadius: 2, 
                  background: forfaitDetails.statut === 'Actif' ? 'rgba(34, 197, 94, 0.2)' : 
                             forfaitDetails.statut === 'En attente' ? 'rgba(251, 191, 36, 0.2)' : 
                             'rgba(239, 68, 68, 0.2)',
                  border: `1px solid ${forfaitDetails.statut === 'Actif' ? 'rgba(34, 197, 94, 0.3)' : 
                                     forfaitDetails.statut === 'En attente' ? 'rgba(251, 191, 36, 0.3)' : 
                                     'rgba(239, 68, 68, 0.3)'}`
                }}>
                  <Typography variant="body2" sx={{ 
                    color: forfaitDetails.statut === 'Actif' ? '#22c55e' : 
                           forfaitDetails.statut === 'En attente' ? '#fbbf24' : 
                           '#ef4444',
                    fontWeight: 600
                  }}>
                    {forfaitDetails.statut}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, px: 3 }}>
              <Grid container spacing={3}>
                {/* Header Image */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    height: 200
                  }}>
                    <img
                      src={getPackageImage(forfaitDetails)}
                      alt={forfaitDetails.titre}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                  </Box>
                </Grid>
                
                {/* Package Details */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 3, 
                    borderRadius: 2, 
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    height: '100%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <FlightIcon sx={{ color: '#10b981', fontSize: 24 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                        Informations du Forfait
                      </Typography>
                    </Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                        {forfaitDetails.titre}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <LocationIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.7)' }} />
                        <Typography variant="h6" sx={{ color: 'white' }}>
                          {forfaitDetails.destination}
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Catégorie</Typography>
                        <Chip
                          label={forfaitDetails.categorie}
                          size="small"
                          sx={{ 
                            backgroundColor: 'rgba(59, 130, 246, 0.2)',
                            color: '#60a5fa',
                            border: '1px solid rgba(59, 130, 246, 0.3)'
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Durée</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TimeIcon sx={{ mr: 0.5, fontSize: 'small', color: 'rgba(255, 255, 255, 0.7)' }} />
                          <Typography variant="body1" fontWeight={500} sx={{ color: 'white' }}>
                            {forfaitDetails.duree}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Statut</Typography>
                        <Typography variant="body1" fontWeight={500} sx={{ color: 'white' }}>
                          {forfaitDetails.statut}
                        </Typography>
                      </Box>
                      
                      {forfaitDetails.dateCreation && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Date de création</Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ color: 'white' }}>
                            {formatDate(forfaitDetails.dateCreation)}
                          </Typography>
                        </Box>
                      )}
                      
                      {forfaitDetails.dateModification && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Dernière modification</Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ color: 'white' }}>
                            {formatDate(forfaitDetails.dateModification)}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Add supplier information */}
                      {forfaitDetails.supplier && (
                        <>
                          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                          
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'white' }}>
                            Fournisseur
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ 
                              background: 'linear-gradient(135deg, #f59e0b, #d97706)', 
                              mr: 2 
                            }}>
                              {forfaitDetails.supplier.nom?.charAt(0) || 'F'}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" fontWeight={500} sx={{ color: 'white' }}>
                                {forfaitDetails.supplier.nom}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                {forfaitDetails.supplier.type} • {forfaitDetails.supplier.ville}, {forfaitDetails.supplier.pays}
                              </Typography>
                            </Box>
                            <Chip 
                              label={forfaitDetails.supplier.statut} 
                              size="small"
                              sx={{ 
                                ml: 'auto',
                                backgroundColor: forfaitDetails.supplier.statut === 'Actif' 
                                  ? 'rgba(34, 197, 94, 0.2)' 
                                  : 'rgba(239, 68, 68, 0.2)',
                                color: forfaitDetails.supplier.statut === 'Actif' ? '#4ade80' : '#f87171',
                                border: `1px solid ${forfaitDetails.supplier.statut === 'Actif' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                              }}
                            />
                          </Box>
                          
                          {forfaitDetails.supplier.telephone && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Téléphone</Typography>
                              <Typography variant="body2" sx={{ color: 'white' }}>{forfaitDetails.supplier.telephone}</Typography>
                            </Box>
                          )}
                          
                          {forfaitDetails.supplier.email && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Email</Typography>
                              <Typography variant="body2" sx={{ color: 'white' }}>{forfaitDetails.supplier.email}</Typography>
                            </Box>
                          )}
                        </>
                      )}
                      
                      <Divider sx={{ my: 2 }} />
                  </Box>
                </Grid>
                
                {/* Price and Booking Info */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    height: '100%', 
                    borderRadius: 2, 
                    background: 'rgba(255, 255, 255, 0.1)', 
                    border: '1px solid rgba(255, 255, 255, 0.2)', 
                    color: 'white' 
                  }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Informations de tarification
                      </Typography>
                      
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'rgba(255,255,255,0.15)', 
                        borderRadius: 2, 
                        mb: 3, 
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                          Prix par personne
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {formatCurrency(forfaitDetails.prix)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                          {forfaitDetails.prix && settings.taxRate ? `TTC (dont TVA ${settings.taxRate}%)` : 'TTC'}
                        </Typography>
                      </Box>
                      
                      {/* Only show inclusions if they were explicitly added during creation */}
                      {forfaitDetails.inclusions && Array.isArray(forfaitDetails.inclusions) && forfaitDetails.inclusions.length > 0 && (
                        <>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Ce qui est inclus:
                          </Typography>
                          
                          <List dense sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, mb: 2 }}>
                            {(forfaitDetails.inclusions || []).map((item, index) => (
                              <ListItem key={index}>
                                <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                                  <CheckCircleIcon fontSize="small" />
                                </ListItemIcon>
                                <MuiListItemText 
                                  primary={item}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}
                      
                      {/* Removing the "Réserver ce forfait" button as it doesn't have functionality */}
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Description */}
                {forfaitDetails.description && (
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 2, 
                      background: 'rgba(255, 255, 255, 0.1)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)' 
                    }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                          Description
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'white' }}>
                          {forfaitDetails.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                
                {/* Related Data */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    borderRadius: 2, 
                    background: 'rgba(255, 255, 255, 0.1)', 
                    border: '1px solid rgba(255, 255, 255, 0.2)' 
                  }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                        Données associées
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Paper sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            background: 'rgba(255, 255, 255, 0.1)', 
                            border: '1px solid rgba(255, 255, 255, 0.2)' 
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <FlightIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.7)' }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>
                                Réservations
                              </Typography>
                            </Box>
                            
                            {(forfaitDetails.reservations && forfaitDetails.reservations.length > 0) ? (
                              <List dense>
                                {(forfaitDetails.reservations || []).map((reservation, index) => (
                                  <ListItem key={index}>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                      {reservation.id}: {formatDate(reservation.dateDepart)} - {reservation.clientNom} 
                                      ({reservation.nombrePassagers} passager{reservation.nombrePassagers > 1 ? 's' : ''})
                                    </Typography>
                                  </ListItem>
                                ))}
                              </List>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Aucune réservation associée
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Paper sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            background: 'rgba(255, 255, 255, 0.1)', 
                            border: '1px solid rgba(255, 255, 255, 0.2)' 
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <PersonPinIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.7)' }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>
                                Clients intéressés
                              </Typography>
                            </Box>
                            
                            {(forfaitDetails.clientsInteresses && forfaitDetails.clientsInteresses.length > 0) ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {(forfaitDetails.clientsInteresses || []).map((client, index) => (
                                  <Chip 
                                    key={index}
                                    avatar={<Avatar sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}>{client.prenom?.[0] || client.nom?.[0] || 'C'}</Avatar>}
                                    label={`${client.prenom || ''} ${client.nom || 'Client'}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                      color: 'white',
                                      border: '1px solid rgba(255, 255, 255, 0.3)'
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Aucun client intéressé
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={handleCloseDetailsDialog} 
                variant="outlined" 
                sx={{ 
                  borderRadius: 2,
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Fermer
              </Button>
              <Button 
                onClick={() => { handleOpenDialog(forfaitDetails); handleCloseDetailsDialog(); }}
                variant="contained" 
                startIcon={<EditIcon />}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
                  }
                }}
              >
                Modifier
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog for Deletion */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={cancelDeleteForfait}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirmation de suppression</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Êtes-vous sûr de vouloir supprimer ce forfait ? Cette action est irréversible.
            {deleteConfirmDialog.hasReservations && (
              <Box sx={{ mt: 2, color: 'error.main' }}>
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                <Typography component="span" fontWeight="bold">
                  Attention: Ce forfait a des réservations associées.
                </Typography>
                <Typography sx={{ mt: 1 }}>
                  En supprimant ce forfait, toutes les réservations associées seront également supprimées.
                  Cette action est irréversible et pourrait affecter les données clients et financières.
                </Typography>
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={cancelDeleteForfait} sx={{ borderRadius: 2, color: 'white' }}>
            Annuler
          </Button>
          <Button 
            onClick={confirmDeleteForfait} 
            variant="contained" 
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              color: 'white'
            }}
          >
            {deleteConfirmDialog.hasReservations ? 'Supprimer forfait et réservations' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default Forfaits;